import {
  ChatApiUrls,
  ApiFileInfo,
  ChatConnectedAccountPayload,
  DifyApiResponse,
  HistoryResponse,
  FeedbackPayload,
  RenamePayload,
  StopStreamingPayload,
  ChatSession,
  CaptionApiResponse,
} from '@/types/chat';
import { ERROR_MESSAGES, formatDifyErrorMessage } from '@/lib/dify/errorUtils';

/**
 * Parameters for sending a message
 */
export interface SendMessageParams {
  message: string;
  conversationId?: string;
  userId: string;
  files?: ApiFileInfo[];
  connected_account?: ChatConnectedAccountPayload;
}

/**
 * Service interface for chat API operations
 * This abstraction allows for easy mocking in tests and swapping implementations
 */
export interface ChatApiService {
  /**
   * Send a message and get a streaming response
   */
  sendMessage(params: SendMessageParams): Promise<ReadableStream>;

  /**
   * Stop an ongoing streaming request
   */
  stopStreaming(taskId: string, userId: string): Promise<void>;

  /**
   * Fetch conversations for a user
   */
  fetchConversations(userId: string, limit?: number): Promise<any>;

  /**
   * Fetch message history for a conversation
   */
  fetchHistory(conversationId: string, userId?: string): Promise<HistoryResponse>;

  /**
   * Rename a conversation
   */
  renameConversation(conversationId: string, newTitle: string, userId: string): Promise<any>;

  /**
   * Delete a conversation
   */
  deleteConversation(conversationId: string, userId: string): Promise<boolean>;

  /**
   * Upload a file
   */
  uploadFile(file: File, userId: string): Promise<ApiFileInfo>;

  /**
   * Get caption for a file
   */
  captionFile(file: File, userId: string): Promise<CaptionApiResponse>;

  /**
   * Upload a URL as a file
   */
  uploadUrlAsFile(url: string, userId: string): Promise<ApiFileInfo[]>;

  /**
   * Get caption for a URL
   */
  captionUrl(url: string, userId: string): Promise<CaptionApiResponse>;

  /**
   * Post feedback for a message
   */
  postFeedback(difyMessageId: string, rating: 'like' | 'dislike', userId: string): Promise<any>;

  /**
   * Create a remote conversation (if needed)
   */
  createRemoteConversation(userId?: string): Promise<ChatSession | null>;
}

/**
 * Production implementation of ChatApiService
 * Uses real API endpoints
 */
export function createChatApiService(apiUrls: ChatApiUrls): ChatApiService {
  const {
    chatApiUrl,
    conversationsApiUrl,
    messagesFeedbackApiUrl,
    uploadApiUrl,
    captionApiUrl,
  } = apiUrls;

  return {
    async sendMessage(params: SendMessageParams): Promise<ReadableStream> {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 300000); // 5 minute timeout

      const requestBody = {
        message: params.message,
        conversationId: params.conversationId,
        userId: params.userId,
        files: params.files,
        connected_account: params.connected_account,
      };

      console.log("Sending request to", chatApiUrl, requestBody);

      try {
        const response = await fetch(chatApiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'text/event-stream',
          },
          body: JSON.stringify(requestBody),
          signal: controller.signal,
          cache: 'no-store',
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          const errorText = await response.text();
          console.error("API Error:", response.status, errorText);

          // Preserve 429 status and extract rate limit info from response
          if (response.status === 429) {
            // Use centralized rate limit error creation
            const { createRateLimitError } = await import('@/lib/rateLimit/types');

            let rateLimitInfo: { limit?: number | null; used?: number | null; remaining?: number | null } = {};

            // Try to parse rate limit info from response body
            try {
              const errorData = JSON.parse(errorText);
              rateLimitInfo = {
                limit: errorData.limit ?? null,
                used: errorData.used ?? null,
                remaining: errorData.remaining ?? null,
              };
            } catch {
              // If parsing fails, keep defaults
            }

            const rateLimitError = createRateLimitError(
              errorText,
              { ...rateLimitInfo, isMonthlyLimit: true },
              response
            );

            throw rateLimitError;
          }

          // Use error formatting for Dify errors
          const userFriendlyMessage = formatDifyErrorMessage(response.status, errorText);

          throw new Error(userFriendlyMessage);
        }

        if (!response.body) {
          throw new Error(ERROR_MESSAGES.CHAT.NO_RESPONSE_BODY);
        }

        return response.body;
      } catch (error: any) {
        clearTimeout(timeoutId);
        if (error.name === 'AbortError') {
          throw new Error(ERROR_MESSAGES.CHAT.TIMEOUT);
        }
        throw error;
      }
    },

    async stopStreaming(taskId: string, userId: string): Promise<void> {
      console.log(`Stopping streaming for task ID: ${taskId}`);
      const response = await fetch(`${chatApiUrl}/${taskId}/stop`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ user: userId } as StopStreamingPayload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Failed to stop streaming: ${errorText}`);
        throw new Error(ERROR_MESSAGES.CHAT.STOP_STREAMING_FAILED);
      }

      const result = await response.json();
      console.log('Streaming stopped successfully:', result);
    },

    async fetchConversations(userId: string, limit: number = 20): Promise<any> {
      const apiUrl = `${conversationsApiUrl}?user=${userId}&limit=${limit}&timestamp=${Date.now()}`;
      try {
        console.time('fetch-conversations-api');
        const response = await fetch(apiUrl, {
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache',
          },
        });
        console.timeEnd('fetch-conversations-api');

        if (!response.ok) {
          const errorText = await response.text();
          console.error("Failed to fetch conversations:", errorText);
          throw new Error(ERROR_MESSAGES.CHAT.FETCH_HISTORY_FAILED);
        }

        const data = await response.json();
        console.log("Conversations API response received:", data);
        return data;
      } catch (error) {
        console.error("Error fetching conversations:", error);
        return { data: [] };
      }
    },

    async fetchHistory(conversationId: string, userId?: string): Promise<HistoryResponse> {
      try {
        if (!userId) {
          console.warn("fetchHistory called without a userId.");
        }
        console.log("Fetching history for conversation:", conversationId, "with user:", userId);
        const response = await fetch(`${conversationsApiUrl}/${conversationId}/messages?user=${userId}`);

        if (!response.ok) {
          const errorText = await response.text();
          console.error("Failed to fetch message history:", errorText);
          throw new Error(ERROR_MESSAGES.CHAT.FETCH_HISTORY_FAILED);
        }

        return await response.json();
      } catch (error) {
        console.error("Error fetching message history:", error);
        return { data: [] };
      }
    },

    async renameConversation(conversationId: string, newTitle: string, userId: string): Promise<any> {
      console.log(`Attempting to rename conversation on Dify, conversationId: ${conversationId} newTitle: ${newTitle} userId: ${userId}`);
      try {
        if (!conversationId || !newTitle || !userId) {
          throw new Error(ERROR_MESSAGES.GENERAL.FETCH_FAILED);
        }

        const response = await fetch(`${conversationsApiUrl}/${conversationId}/name`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ name: newTitle, userId: userId } as RenamePayload),
        });

        if (!response.ok) {
          const errorData = await response.text();
          console.error("Error from API during rename:", errorData);
          throw new Error(`Failed to rename conversation: ${errorData}`);
        }

        const result = await response.json();
        console.log("Conversation renamed on Dify successfully:", result);
        return result;
      } catch (error) {
        console.error("Exception during rename operation:", error);
        throw error;
      }
    },

    async deleteConversation(conversationId: string, userId: string): Promise<boolean> {
      console.log(`Attempting to delete conversation ${conversationId} for user ${userId}`);
      // NOTE: Dify might not have a direct DELETE endpoint for conversations accessible this way.
      // The current implementation deletes locally and marks ID as deleted.
      console.warn(`Server-side deletion for conversation ${conversationId} not implemented or not available.`);
      return true; // Assume success for local deletion logic to proceed
    },

    async uploadFile(file: File, userId: string): Promise<ApiFileInfo> {
      // File compression is handled by the hook layer, not the service
      // This service just handles the upload
      const formData = new FormData();
      formData.append('file', file, file.name);
      formData.append('user', userId);

      try {
        console.log('[ChatApiService] Uploading file via chat upload route', {
          uploadApiUrl,
          userId,
          fileName: file.name,
          sizeBytes: file.size,
        });
        const response = await fetch(uploadApiUrl, {
          method: 'POST',
          body: formData,
        });

        console.log('[ChatApiService] Upload route response received', {
          status: response.status,
          ok: response.ok,
          statusText: response.statusText,
        });
        if (!response.ok) {
          const errorText = await response.text();
          console.error("File upload error:", response.statusText, errorText);
          throw new Error(`File upload failed: ${errorText}`);
        }

        const fileData = await response.json();
        console.log("[ChatApiService] File upload response payload", {
          id: fileData?.id,
          name: fileData?.name,
          url: fileData?.url,
          size: fileData?.size,
          type: fileData?.mime_type,
        });
        const apiFileInfo: ApiFileInfo = {
          id: fileData.id,
          name: fileData.name,
          type: fileData.mime_type || file.type,
          size: fileData.size,
          url: fileData.url,
          extension: fileData.extension,
          created_at: fileData.created_at,
        };
        return apiFileInfo;
      } catch (error) {
        console.error("[ChatApiService] Error uploading file", {
          userId,
          fileName: file.name,
          error,
        });
        throw error;
      }
    },

    async captionFile(file: File, userId: string): Promise<CaptionApiResponse> {
      const formData = new FormData();
      formData.append('file', file, file.name);
      formData.append('user', userId);

      console.log(`Requesting caption for file: ${file.name} by user: ${userId}`);

      try {
        const response = await fetch(captionApiUrl, {
          method: 'POST',
          body: formData,
        });

        if (!response.ok) {
          let errorDetails = `HTTP ${response.status}`;
          let responseText = '';
          try {
            responseText = await response.text();
            console.error(`Caption API error response for ${file.name}:`, response.status, responseText);
            if (responseText.trim().startsWith('{') && responseText.trim().endsWith('}')) {
              try {
                const errorJson = JSON.parse(responseText);
                errorDetails = errorJson.error || errorJson.message || `HTTP ${response.status}`;
              } catch (parseError) {
                errorDetails = responseText.substring(0, 500);
              }
            } else {
              errorDetails = responseText.substring(0, 500);
            }
          } catch (readError) {
            console.error(`Caption API error for ${file.name}: Status ${response.status}. Failed to read response body.`);
          }
          return { caption: null, error: `Captioning failed: ${errorDetails}` };
        }

        let result: CaptionApiResponse;
        const responseText = await response.text();

        try {
          result = JSON.parse(responseText);
        } catch (jsonError: any) {
          console.error(`Failed to parse successful caption API response as JSON for ${file.name}:`, jsonError);
          console.error(`Received text: ${responseText.substring(0, 500)}...`);
          return { caption: null, error: `Captioning request succeeded but response was not valid JSON.` };
        }

        if (result.error) {
          console.error(`Captioning failed for ${file.name} (reported by API): ${result.error}`);
          return { caption: null, error: result.error };
        } else if (result.caption !== undefined && result.caption !== null) {
          console.log(`Caption received for ${file.name}: "${result.caption.substring(0, 100)}..."`);
          return { caption: result.caption };
        } else {
          console.log(`No caption returned or captioning skipped for ${file.name}.`);
          return { caption: null };
        }
      } catch (error: any) {
        console.error(`Network or other error calling caption API for ${file.name}:`, error);
        if (error instanceof SyntaxError) {
          return { caption: null, error: `Captioning request failed: Invalid response format.` };
        }
        if (error.name === 'AbortError') {
          return { caption: null, error: `Captioning request timed out.` };
        }
        return { caption: null, error: `Captioning request failed: ${error.message}` };
      }
    },

    async uploadUrlAsFile(url: string, userId: string): Promise<ApiFileInfo[]> {
      try {
        // Helper function to upload a single URL
        const uploadSingleUrl = async (url: string, userId: string): Promise<ApiFileInfo> => {
          console.log(`[ChatApiService] Uploading URL as file: ${url} for user: ${userId}`);
          const response = await fetch(uploadApiUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ url, user: userId }),
          });

          if (!response.ok) {
            const errorText = await response.text();
            console.error("[ChatApiService] URL upload error:", response.statusText, errorText);
            throw new Error(`URL upload failed: ${errorText}`);
          }

          const fileData = await response.json();
          console.log("[ChatApiService] URL upload response:", fileData);

          // Import utility function
          const { getMediaTypeFromUrl } = await import('@/lib/urlUtils');
          const { isDifyUuid } = await import('@/lib/dify/utils');

          const determinedType = fileData.type || getMediaTypeFromUrl(url) || 'custom';

          const apiFileInfo: ApiFileInfo = {
            id: fileData.id && isDifyUuid(fileData.id) ? fileData.id : null,
            name: fileData.name || new URL(url).pathname.split('/').pop()?.split('?')[0] || url,
            type: determinedType,
            size: fileData.size || 0,
            url: fileData.url || url,
            extension: fileData.extension || url.split('.').pop()?.split('?')[0] || '',
            created_at: fileData.created_at || Date.now(),
            caption: fileData.caption,
          };

          console.log("[ChatApiService] Constructed ApiFileInfo:", apiFileInfo);
          return apiFileInfo;
        };

        const fileInfo = await uploadSingleUrl(url, userId);
        return [fileInfo];
      } catch (error) {
        console.error("[ChatApiService] Error uploading URL as file:", error);
        throw error;
      }
    },

    async captionUrl(url: string, userId: string): Promise<CaptionApiResponse> {
      console.log(`Requesting caption for URL: ${url} by user: ${userId}`);

      try {
        const response = await fetch(captionApiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ url, user: userId }),
        });

        if (!response.ok) {
          let errorDetails = `HTTP ${response.status}`;
          try {
            const responseText = await response.text();
            console.error(`Caption API error for URL ${url}:`, responseText);
            if (responseText.startsWith('{') && responseText.endsWith('}')) {
              const errorJson = JSON.parse(responseText);
              errorDetails = errorJson.error || errorDetails;
            } else {
              errorDetails = responseText.substring(0, 200);
            }
          } catch {/* Ignore read errors */}
          return { caption: null, error: `Captioning failed: ${errorDetails}` };
        }

        try {
          const result = await response.json();
          if (result.error) {
            return { caption: null, error: result.error };
          } else {
            return { caption: result.caption };
          }
        } catch (parseError) {
          return { caption: null, error: 'Invalid response format from caption API' };
        }
      } catch (error: any) {
        return { caption: null, error: `Captioning request failed: ${error.message}` };
      }
    },

    async postFeedback(difyMessageId: string, rating: 'like' | 'dislike', userId: string): Promise<any> {
      try {
        const response = await fetch(`${messagesFeedbackApiUrl}/${difyMessageId}/feedbacks`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ rating, userId } as FeedbackPayload),
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error(`Failed to send feedback for message ${difyMessageId}:`, errorText);
          throw new Error(`Failed to send feedback: ${errorText}`);
        }

        const result = await response.json();
        console.log(`Feedback sent for message ${difyMessageId}: ${rating}`, result);
        return result;
      } catch (error) {
        console.error("Error sending feedback:", error);
        throw error;
      }
    },

    async createRemoteConversation(userId?: string): Promise<ChatSession | null> {
      // This function might not be needed if conversations are created implicitly
      // by the first message sent via sendMessage.
      // If Dify requires explicit creation, implement the API call here.
      // For now, we assume implicit creation.
      console.log("Remote conversation creation (implicit) for user:", userId);
      return null; // Placeholder
    },
  };
}

export { createMockChatApiService } from './__mocks__/chatApiService';
